import Phaser from 'phaser';
import { attachActorNormalMaps } from '../light/normal-bake';
import { queueActorTextures, THIRD_PIG, type PreparedActor } from '../anim/actor-clipset';
import { PREPARED_ACTOR_KEY } from './PreloadScene';
import { SCENE_REGISTRY_KEY } from '../scene/scene-registry';
import type { SceneDef } from '../scene/scene-format';
import { queueBeatMedia } from './EditRunner';
import { queueNpcTextures, loadedNpcRig } from '../anim/npc-clipset';

// BootScene — the second of the actor's two loader runs (PreloadScene fetched and
// normalized the clip-set manifest AND the active Scene record; this fetches the
// part PNGs the manifest names and the plate the Scene names). Then it bakes the
// procedural textures and hands off to the SceneRunner.
//
// Order 16.2's lighting pipeline still holds: every part texture gets a normal
// map so the night-alley key rakes across the figure. The pig carries no
// authored normal maps (Character Animator puppets are flat art), so each one is
// derived from that part's own alpha silhouette — see light/normal-bake.ts.
//
// The MARQUEE and RIPPLE textures remain procedural: they are lighting apparatus
// (an emissive sign and a displacement map), not artwork.

export const TEX_MARQUEE = 'marquee';
export const TEX_RIPPLE = 'ripple';
/** The painted plate, drawn 1:1 over the level. Its image path is a property of
 *  the active Scene (plate.image), not a fixed asset — the loader supplies it. */
export const TEX_BACKDROP = 'backdrop-plate';

/** Handed to AlleyScene through the Scene data registry — it holds the parsed,
 *  feet-origin-normalized clip set and the scale the Rig should draw at. */
export const ACTOR_REGISTRY_KEY = 'actor';

export class BootScene extends Phaser.Scene {
  constructor() {
    super('Boot');
  }

  private prepared!: PreparedActor;

  preload(): void {
    this.prepared = this.registry.get(PREPARED_ACTOR_KEY) as PreparedActor;
    if (!this.prepared) throw new Error('BootScene: no prepared actor — PreloadScene must run first');
    const def = this.registry.get(SCENE_REGISTRY_KEY) as SceneDef | undefined;
    if (!def) throw new Error('BootScene: no SceneDef — PreloadScene must run first');
    // Once per run: after a location transition the parts are already textures.
    if (!this.textures.exists(this.prepared.textureKeys[0])) {
      queueActorTextures(this, THIRD_PIG, this.prepared);
    }
    // The plate is the active Scene's, not a fixed asset — and it CHANGES on a
    // transition, so the previous location's must be evicted from under the key.
    if (this.textures.exists(TEX_BACKDROP)) this.textures.remove(TEX_BACKDROP);
    this.load.image(TEX_BACKDROP, def.plate.image);
    // A scene with an edit names its watched beats' media (insert + line).
    if (def.beats) queueBeatMedia(this, def.id, def.beats);
    // ...and its cast names their rigs' part textures.
    if (def.npcs) queueNpcTextures(this, def.npcs.map((n) => n.rig));
  }

  create(): void {
    if (!this.textures.exists(TEX_MARQUEE)) this.makeMarqueeTexture();
    if (!this.textures.exists(TEX_RIPPLE)) this.makeRippleTexture();

    if (!this.registry.get(ACTOR_REGISTRY_KEY)) {
      // Derive one normal map per part from its artwork, and attach it as the
      // texture's data source — where Phaser's Lighting component reads it.
      attachActorNormalMaps(this, this.prepared.textureKeys);
      this.registry.set(ACTOR_REGISTRY_KEY, this.prepared);
    }

    // The cast's rigs light the same way. Normal maps attach once per texture;
    // the guard key is the rig's own normal-map marker in the registry.
    const def = this.registry.get(SCENE_REGISTRY_KEY) as SceneDef;
    for (const rigId of new Set((def.npcs ?? []).map((n) => n.rig))) {
      const litKey = `npc:lit:${rigId}`;
      if (this.registry.get(litKey)) continue;
      attachActorNormalMaps(this, loadedNpcRig(this, rigId).textureKeys);
      this.registry.set(litKey, true);
    }

    this.scene.start('Play');
  }

  /** Bake the emissive marquee sign — the puddle-reflection shot's light source.
   *  A studio-yellow sign body with a couple of "bulb" rows and a soft glow halo,
   *  so its flipped reflection reads as a recognisable bright shape in the water. */
  private makeMarqueeTexture(): void {
    const W = 72;
    const H = 34;
    const g = this.add.graphics();

    // Soft glow halo (stacked translucent rounded rects → cheap bloom).
    for (let i = 5; i >= 1; i--) {
      g.fillStyle(0xffd583, 0.06);
      g.fillRoundedRect(8 - i * 1.4, 6 - i * 1.4, W - 16 + i * 2.8, 16 + i * 2.8, 6);
    }
    // Sign plate.
    g.fillStyle(0x2a2620, 1);
    g.fillRoundedRect(8, 6, W - 16, 16, 4);
    g.lineStyle(1.5, 0xffe6a8, 0.9);
    g.strokeRoundedRect(8, 6, W - 16, 16, 4);
    // Bright face + a red accent stripe (neon).
    g.fillStyle(0xffd583, 0.95);
    g.fillRoundedRect(12, 9, W - 24, 5, 2);
    g.fillStyle(0xcc444e, 0.9);
    g.fillRoundedRect(12, 16, W - 24, 3, 1.5);
    // Bulb dots along the bottom edge.
    g.fillStyle(0xffe6a8, 1);
    for (let x = 12; x < W - 12; x += 8) g.fillCircle(x, 25, 1.6);

    g.generateTexture(TEX_MARQUEE, W, H);
    g.destroy();
  }

  /** Bake a ripple displacement map: smooth horizontal wave bands with a slight
   *  vertical component, encoded in R (x-offset) and G (y-offset) around 0.5. The
   *  Displacement filter samples this to warp the reflection; animating its
   *  magnitude (PuddleReflection.update) makes the water wobble. */
  private makeRippleTexture(): void {
    const S = 128;
    const canvas = document.createElement('canvas');
    canvas.width = S;
    canvas.height = S;
    const ctx = canvas.getContext('2d')!;
    const img = ctx.createImageData(S, S);
    const d = img.data;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        // Horizontal bands (vary with y) dominate — water ripples are near-flat.
        const rx = Math.sin(y * 0.20) * 0.5 + Math.sin(y * 0.07 + x * 0.03) * 0.5;
        const ry = Math.sin(x * 0.13 + y * 0.05) * 0.6;
        const i = (y * S + x) * 4;
        d[i] = (rx * 0.5 + 0.5) * 255;
        d[i + 1] = (ry * 0.5 + 0.5) * 255;
        d[i + 2] = 128;
        d[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    this.textures.addCanvas(TEX_RIPPLE, canvas);
  }
}
